/**
 * Image Compression Service
 * 
 * Provides lossless and lossy image compression using Canvas API
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 to 1.0 for lossy compression
  format?: 'jpeg' | 'png' | 'webp';
  lossless?: boolean; // true for lossless compression
}

export interface CompressionResult {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  format: string;
  dataUrl: string;
  blob: Blob;
}

export class ImageCompressionService {
  private static instance: ImageCompressionService;

  static getInstance(): ImageCompressionService {
    if (!ImageCompressionService.instance) {
      ImageCompressionService.instance = new ImageCompressionService();
    }
    return ImageCompressionService.instance;
  }

  /**
   * Compress an image file with specified options
   */
  async compressImage(
    file: File, 
    options: CompressionOptions = {}
  ): Promise<CompressionResult> {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.8,
      format = 'jpeg',
      lossless = false
    } = options;

    return new Promise((resolve, reject) => {
      // Check if canvas is supported (important for Android browsers)
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas API not supported in this browser'));
        return;
      }

      const img = new Image();

      // Set timeout for image loading (prevents hanging on some Android devices)
      const loadTimeout = setTimeout(() => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Image loading timeout - may be too large or unsupported format'));
      }, 30000); // 30 second timeout

      img.onload = () => {
        clearTimeout(loadTimeout);
        try {
          // Calculate new dimensions maintaining aspect ratio
          const { width, height } = this.calculateDimensions(
            img.width,
            img.height,
            maxWidth,
            maxHeight
          );

          // Check for maximum canvas size (some Android devices have limits)
          const maxCanvasSize = 4096; // Common limit on mobile devices
          if (width > maxCanvasSize || height > maxCanvasSize) {
            URL.revokeObjectURL(img.src);
            reject(new Error(`Image dimensions (${width}x${height}) exceed maximum canvas size (${maxCanvasSize}x${maxCanvasSize})`));
            return;
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw and compress image
          ctx.drawImage(img, 0, 0, width, height);

          // Determine output format
          const outputFormat = this.getOutputFormat(format, file.type, lossless);
          
          // Convert to blob with error handling
          try {
            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(img.src);
                
                if (!blob) {
                  reject(new Error('Failed to compress image - canvas.toBlob returned null'));
                  return;
                }

                // Create data URL for preview
                const reader = new FileReader();
                
                reader.onerror = () => {
                  reject(new Error('Failed to read compressed image data'));
                };
                
                reader.onload = () => {
                  const result: CompressionResult = {
                    originalSize: file.size,
                    compressedSize: blob.size,
                    compressionRatio: Math.round((1 - blob.size / file.size) * 100),
                    format: outputFormat,
                    dataUrl: reader.result as string,
                    blob: blob
                  };

                  console.log(`📷 Image compressed: ${file.name}`);
                  console.log(`   Original: ${this.formatFileSize(file.size)}`);
                  console.log(`   Compressed: ${this.formatFileSize(blob.size)}`);
                  console.log(`   Reduction: ${result.compressionRatio}%`);

                  resolve(result);
                };
                
                reader.readAsDataURL(blob);
              },
              outputFormat,
              lossless ? 1.0 : quality
            );
          } catch (blobError: any) {
            URL.revokeObjectURL(img.src);
            reject(new Error(`Failed to convert canvas to blob: ${blobError?.message || blobError}`));
          }
        } catch (error: any) {
          URL.revokeObjectURL(img.src);
          reject(new Error(`Image compression error: ${error?.message || error}`));
        }
      };

      img.onerror = (error) => {
        clearTimeout(loadTimeout);
        URL.revokeObjectURL(img.src);
        reject(new Error(`Failed to load image for compression: ${error?.toString() || 'Unknown error'}`));
      };

      // Set image source
      try {
        img.src = URL.createObjectURL(file);
      } catch (error: any) {
        clearTimeout(loadTimeout);
        reject(new Error(`Failed to create object URL: ${error?.message || error}`));
      }
    });
  }

  /**
   * Compress multiple images
   */
  async compressImages(
    files: File[],
    options: CompressionOptions = {}
  ): Promise<CompressionResult[]> {
    const results = await Promise.all(
      files.map(file => this.compressImage(file, options))
    );
    
    const totalOriginalSize = results.reduce((sum, result) => sum + result.originalSize, 0);
    const totalCompressedSize = results.reduce((sum, result) => sum + result.compressedSize, 0);
    const totalReduction = Math.round((1 - totalCompressedSize / totalOriginalSize) * 100);

    console.log(`📷 Batch compression complete:`);
    console.log(`   Total original: ${this.formatFileSize(totalOriginalSize)}`);
    console.log(`   Total compressed: ${this.formatFileSize(totalCompressedSize)}`);
    console.log(`   Total reduction: ${totalReduction}%`);

    return results;
  }

  /**
   * Calculate optimal dimensions maintaining aspect ratio
   */
  private calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let width = originalWidth;
    let height = originalHeight;

    // Scale down if image is larger than max dimensions
    if (width > maxWidth || height > maxHeight) {
      const aspectRatio = width / height;

      if (width > height) {
        width = Math.min(width, maxWidth);
        height = width / aspectRatio;
      } else {
        height = Math.min(height, maxHeight);
        width = height * aspectRatio;
      }
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * Determine optimal output format
   */
  private getOutputFormat(
    requestedFormat: string,
    originalType: string,
    lossless: boolean
  ): string {
    // For lossless compression, prefer PNG
    if (lossless) {
      return 'image/png';
    }

    // For lossy compression, use requested format or JPEG as default
    switch (requestedFormat) {
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'jpeg':
      default:
        return 'image/jpeg';
    }
  }

  /**
   * Format file size in human readable format
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get compression recommendations based on file size and type
   */
  getCompressionRecommendations(file: File): CompressionOptions {
    const sizeMB = file.size / (1024 * 1024);
    
    // For very large images (>5MB), use aggressive compression
    if (sizeMB > 5) {
      return {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.7,
        format: 'jpeg',
        lossless: false
      };
    }
    
    // For large images (2-5MB), use moderate compression
    if (sizeMB > 2) {
      return {
        maxWidth: 2048,
        maxHeight: 1536,
        quality: 0.8,
        format: 'jpeg',
        lossless: false
      };
    }
    
    // For medium images (0.5-2MB), use light compression
    if (sizeMB > 0.5) {
      return {
        maxWidth: 2560,
        maxHeight: 1920,
        quality: 0.85,
        format: 'jpeg',
        lossless: false
      };
    }
    
    // For small images (<0.5MB), use lossless compression
    return {
      maxWidth: 3840,
      maxHeight: 2160,
      quality: 1.0,
      format: 'png',
      lossless: true
    };
  }

  /**
   * Check if image compression is supported
   */
  isSupported(): boolean {
    return !!(document.createElement('canvas').getContext && window.FileReader);
  }

  /**
   * Get supported image formats
   */
  getSupportedFormats(): string[] {
    return ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  }

  /**
   * Validate if file can be compressed
   */
  canCompress(file: File): boolean {
    return this.getSupportedFormats().includes(file.type.toLowerCase());
  }
}
